/**
 * Shared "rebuild a SAVED conversation into renderable turns" for the Agent SDK
 * door pages (Quality Co-Pilot, Tech Support Co-Pilot, …).
 *
 * A persisted `ConversationDetail` (its messages + tool-call audit rows) becomes
 * a flat list of turns whose assistant entries carry the SAME thinking / tool /
 * answer trace the live stream produces — via the Agent Console's
 * `expandAssistantBlocks`, which pairs each persisted `tool_use` block to its
 * audit row by id. The voice rule is applied here too: a `voice_reply` tool-use
 * becomes the spoken rundown (`spoken`) and is dropped from the visible trace.
 *
 * Each door maps these neutral turns onto its OWN turn type (adding e.g. the
 * admin usage line, or `viaVoice`) — this module owns the one shared walk so a
 * resumed thread renders identically to one just streamed, across every door.
 */
import {
  expandAssistantBlocks,
  toolCallViewFromRead,
  type AssistantBlock,
  type ConversationDetail,
  type MessageRead,
  type ToolCallView,
} from '../../services/agentConsole';
import { isVoiceReplyTool } from '../../services/qualityCopilot';
import { liftAgentFileCards, type AgentFileData } from './agentFileCards';
import {
  attachmentsByMessageId,
  type MessageAttachment,
} from '../chat/messageAttachments';

/**
 * Marker stamped on the auto-draft's initiating user message (persisted in its
 * `raw_blocks` by the backend orchestrator). Mirrors
 * `AUTO_DRAFT_KICKOFF_TYPE` in services/agent_console/runtime/orchestrator.py.
 */
export const AUTO_DRAFT_KICKOFF_TYPE = 'auto_draft_kickoff';

/** True when a user message was typed by the auto-draft pipeline, not the human. */
export function isAutoDraftKickoff(m: MessageRead): boolean {
  return (m.raw_blocks ?? []).some((b) => b?.type === AUTO_DRAFT_KICKOFF_TYPE);
}

export interface RebuiltTurn {
  id: string;
  role: 'user' | 'assistant';
  /** User message content (user turns only). */
  text?: string;
  /**
   * Set on a user turn the auto-draft pipeline initiated (its "A new email came
   * in…" kickoff), so a door can render it as a system note instead of the
   * human's words. Undefined for a real, human-typed message.
   */
  kind?: typeof AUTO_DRAFT_KICKOFF_TYPE;
  /** Assistant spoken rundown — the `voice_reply` summary, or null if none. */
  spoken?: string | null;
  /** Assistant trace (thinking / tool / answer), with `voice_reply` dropped. */
  blocks?: AssistantBlock[];
  /** The source assistant row, so a door can derive extras (e.g. usage). */
  message?: MessageRead;
  /**
   * When the message was persisted (ISO, from the audit row) — every door
   * shows it on the bubble so a resumed thread reads with real times.
   */
  createdAt?: string | null;
  /** Files that rode in on this user message (chips in the bubble). */
  attachments?: MessageAttachment[];
  /**
   * Files the AGENT attached this turn (the `attach_file` channel) — lifted
   * out of the trace here so every door renders the same file cards on resume.
   * Assistant turns only; undefined when the turn attached nothing.
   */
  agentFiles?: AgentFileData[];
  /**
   * A server-side recovery note persisted as a SYSTEM row right before this
   * assistant turn (today: `context_rollover` — the thread's history no longer
   * fit the model, so this reply started from a fresh context). Assistant turns
   * only; undefined when the turn streamed without one.
   */
  notice?: string;
}

/**
 * Marker on a persisted SYSTEM row that carries a recovery notice (mirrors the
 * `notice` SSE frame the orchestrator audits — services/agent_console/runtime/
 * orchestrator.py::_best_effort_record_notice).
 */
export const NOTICE_ROW_TYPE = 'notice';

/** The notice text when a SYSTEM row is a persisted recovery notice, else null. */
export function noticeFromSystemRow(m: MessageRead): string | null {
  if (m.role !== 'system') return null;
  const block = (m.raw_blocks ?? []).find((b) => b?.type === NOTICE_ROW_TYPE);
  if (!block) return null;
  const text = typeof block.text === 'string' ? block.text : m.content;
  return text ? text : null;
}

/**
 * Walk a saved conversation's messages (ordered by persisted `seq`) + tool-call
 * audit rows into neutral turns. Pure — safe to call on resume.
 */
export function detailToTurns(detail: ConversationDetail): RebuiltTurn[] {
  const toolsById = new Map<string, ToolCallView>();
  for (const tc of detail.tool_calls) {
    if (tc.tool_use_id) toolsById.set(tc.tool_use_id, toolCallViewFromRead(tc));
  }
  // Persisted seq is the source of truth for order (the API returns rows ordered,
  // but sorting keeps this helper independent of that contract).
  const ordered = [...detail.messages].sort((a, b) => a.seq - b.seq);
  // The post-turn claimer stamped each attachment onto its user message row.
  const attachmentsFor = attachmentsByMessageId(detail.attachments);
  const turns: RebuiltTurn[] = [];
  // A persisted recovery notice sits between the user message and the reply it
  // preceded; hold it so the NEXT assistant turn renders it, as it did live.
  let pendingNotice: string | null = null;
  for (const m of ordered) {
    if (m.role === 'user') {
      pendingNotice = null;
      const attachments = attachmentsFor.get(m.id);
      turns.push({
        id: `m${m.seq}`,
        role: 'user',
        text: m.content,
        createdAt: m.created_at ?? null,
        ...(isAutoDraftKickoff(m) ? { kind: AUTO_DRAFT_KICKOFF_TYPE } : {}),
        ...(attachments ? { attachments } : {}),
      });
      continue;
    }
    if (m.role !== 'assistant') {
      // System rows aren't shown in the chat UI — except a recovery notice,
      // which rides onto the assistant turn that followed it.
      const notice = noticeFromSystemRow(m);
      if (notice) pendingNotice = notice;
      continue;
    }
    const expanded = expandAssistantBlocks(m, toolsById).filter(
      (b) => !(b.kind === 'tool' && isVoiceReplyTool(b.tool.rawName)),
    );
    // Successful attach_file frames become file cards (the card replaces the
    // chip); pending/errored attach frames keep their chips in the trace.
    const { blocks, files } = liftAgentFileCards(expanded);
    turns.push({
      id: `m${m.seq}`,
      role: 'assistant',
      spoken: spokenFromRawBlocks(m),
      blocks,
      message: m,
      createdAt: m.created_at ?? null,
      ...(files.length ? { agentFiles: files } : {}),
      ...(pendingNotice ? { notice: pendingNotice } : {}),
    });
    pendingNotice = null;
  }
  return turns;
}

/**
 * Pull the spoken rundown (the `voice_reply` tool's `summary`) out of a saved
 * assistant message's `raw_blocks`. Read straight from the persisted block input
 * — not the tool-call audit table — so the rundown survives even if the audit row
 * is missing. Returns null when the turn had no `voice_reply` (e.g. a text-only
 * Agent Console turn later resumed in a voice door).
 */
export function spokenFromRawBlocks(message: MessageRead): string | null {
  for (const block of message.raw_blocks ?? []) {
    const name = typeof block.name === 'string' ? block.name : '';
    if (block.type === 'tool_use' && isVoiceReplyTool(name)) {
      const input = block.input as { summary?: unknown } | undefined;
      if (typeof input?.summary === 'string' && input.summary) return input.summary;
    }
  }
  return null;
}
